import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { EsgPencilIcon, CrossIcon } from 'assets';
import { EsgSingleSelectControlledField } from 'components';
import { InputTextField } from '../inputTextField';

interface CustomDropdownFieldProps {
    label: string;
    options: Array<{ label: string; value: string }>;
    selectedValue: string;
    placeholder: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    showCustomOption?: boolean;
    disableCustomOption?: boolean;
    sxLabel?: React.CSSProperties;
    sxSelect?: React.CSSProperties;
    sxMenuItem?: React.CSSProperties;
    sx?: React.CSSProperties;
}

const CUSTOM_OPTION_VALUE = 'custom';
const CUSTOM_OPTION_LABEL = 'Custom';


const CustomInputContainer = styled(Box)({
    display: 'flex',
    alignItems: 'flex-end',
    gap: '2px',
    width: '100%',
});

export const CustomDropdownField: React.FC<CustomDropdownFieldProps> = ({
    label,
    options,
    selectedValue,
    placeholder,
    onChange,
    disabled = false,
    showCustomOption = true,
    disableCustomOption = false,
    sxLabel,
    sxSelect,
    sxMenuItem,
    sx
}) => {
    const [isCustomMode, setIsCustomMode] = useState(false);
    const [customValue, setCustomValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Check if current value is custom (not in original options)
    const isCurrentValueCustom = selectedValue &&
        !options.some(option => option.value === selectedValue) &&
        selectedValue !== CUSTOM_OPTION_VALUE;

    // Add custom option to the dropdown options only if showCustomOption is true
    const customOption = {
        label: CUSTOM_OPTION_LABEL,
        value: CUSTOM_OPTION_VALUE,
        icon: <EsgPencilIcon style={{ width: '14px', height: '14px' }} />,
        disabled: disableCustomOption,
        tooltip: disableCustomOption ? "Default unit cannot be changed to custom as data already exists." : undefined
    };

    const optionsWithCustom = showCustomOption ? [
        ...options,
        customOption
    ] : options;

    const handleSelectChange = (value: string | number) => {
        const stringValue = String(value);
        if (stringValue === CUSTOM_OPTION_VALUE) {
            if (disableCustomOption) {
                return;
            }
            setIsCustomMode(true);
            setCustomValue(selectedValue && isCurrentValueCustom ? selectedValue : '');
            // Focus the input after a short delay
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        } else {
            setIsCustomMode(false);
            onChange(stringValue);
        }
    };

    // Reset custom mode when options change
    useEffect(() => {
        if (!isCurrentValueCustom && selectedValue !== CUSTOM_OPTION_VALUE) {
            setIsCustomMode(false);
            setCustomValue('');
        }
    }, [options, selectedValue, isCurrentValueCustom]);

    // If in custom mode, show input field with dropdown toggle
    if (isCustomMode) {
        return (
            <Box sx={sx}>
                <CustomInputContainer>
                    <InputTextField field={{
                        key: label,
                        label,
                        value: customValue,
                        onChange: (value) => {
                            setCustomValue(value);
                            onChange(value);
                        },
                        placeholder
                    }} />
                    <CrossIcon
                        style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            color: '#666',
                            marginBottom: '8px'
                        }}
                        onClick={() => setIsCustomMode(false)}
                    />
                </CustomInputContainer>
            </Box>
        );
    }

    return (
        <EsgSingleSelectControlledField
            sxMenuItem={sxMenuItem}
            sxSelect={sxSelect}
            sx={sx}
            label={label}
            sxLabel={sxLabel}
            options={optionsWithCustom}
            selectedValue={isCurrentValueCustom ? CUSTOM_OPTION_VALUE : (selectedValue || '')}
            setSelectedValue={handleSelectChange}
            placeholder={placeholder}
            disabled={disabled}
        />
    );
};
